<template>
	<div v-if="visible" class="notification-permission-banner">
		<div class="content">
			<strong>Bật thông báo realtime</strong>
			<p>Bạn sẽ nhận được thông báo khi có người gửi yêu cầu ký cho bạn, khi phiên ký hoàn tất, v.v.</p>
			<label class="sound-toggle">
				<input v-model="soundEnabled" type="checkbox" @change="saveSoundPreference" />
				<span>Phát âm báo khi có thông báo mới</span>
			</label>
		</div>
		<div class="actions">
			<button class="btn-skip" type="button" @click="dismiss">Bỏ qua</button>
			<button class="btn-allow" type="button" @click="allow" :disabled="loading">{{ loading ? 'Đang bật...' : 'Cho phép' }}</button>
		</div>
	</div>
</template>

<script>
import { isNotificationSoundEnabled, setNotificationSoundEnabled, unlockNotificationSound } from '@/services/notification-sound';

const DISMISS_KEY = 'notif_permission_dismissed';
const DISMISS_DAYS = 7;
const ENABLE_TIMEOUT = 10000;

function withTimeout(promise, timeout = ENABLE_TIMEOUT) {
	return Promise.race([
		promise,
		new Promise((resolve) => setTimeout(() => resolve(null), timeout)),
	]);
}

export default {
	name: 'NotificationPermissionBanner',
	data() {
		return { visible: false, loading: false, soundEnabled: true };
	},
	async mounted() {
		this.soundEnabled = isNotificationSoundEnabled();
		if (typeof Notification === 'undefined') return;
		const permission = Notification.permission;
		if (permission !== 'default') return;
		const lastDismissed = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10);
		if (lastDismissed && Date.now() - lastDismissed < DISMISS_DAYS * 24 * 3600 * 1000) return;
		this.visible = true;
	},
	methods: {
		async allow() {
			this.loading = true;
			try {
				this.saveSoundPreference();
				unlockNotificationSound();
				const [{ initFcm }, { default: NotificationDevice }] = await Promise.all([
					import('@/services/firebase-messaging'),
					import('@/models/NotificationDevice'),
				]);
				const token = await withTimeout(initFcm());
				if (token) {
					const deviceId = this.deviceId();
					await withTimeout(NotificationDevice.registerDevice({
						platform: 'web',
						fcm_token: token,
						device_id: deviceId,
						browser: this.browserName(),
						os: this.osName(),
					}));
					console.info('[FCM] Notification device registered.', {
						device_id: deviceId,
						token_hash: await this.tokenFingerprint(token),
					});
				} else {
					console.warn('[FCM] Token is empty; device was not registered.');
				}
				this.visible = false;
			} catch (e) {
				console.warn('[FCM] Could not enable notification permission.', e);
				this.visible = false;
			} finally {
				this.loading = false;
			}
		},
		dismiss() {
			localStorage.setItem(DISMISS_KEY, String(Date.now()));
			this.visible = false;
		},
		saveSoundPreference() {
			setNotificationSoundEnabled(this.soundEnabled);
		},
		async tokenFingerprint(token) {
			if (!window.crypto || !window.crypto.subtle) {
				return token.slice(-12);
			}
			const bytes = new TextEncoder().encode(token);
			const digest = await window.crypto.subtle.digest('SHA-256', bytes);
			return Array.from(new Uint8Array(digest))
				.map((byte) => byte.toString(16).padStart(2, '0'))
				.join('')
				.slice(0, 12);
		},
		deviceId() {
			let id = localStorage.getItem('web_device_id');
			if (!id) {
				id = 'web-' + Math.random().toString(36).slice(2, 12);
				localStorage.setItem('web_device_id', id);
			}
			return id;
		},
		browserName() {
			try { return navigator.userAgent.match(/(Chrome|Firefox|Safari|Edge)\/[\d.]+/)?.[0] || ''; } catch (e) { return ''; }
		},
		osName() {
			try { return navigator.platform || ''; } catch (e) { return ''; }
		},
	},
};
</script>

<style scoped>
.notification-permission-banner { position: fixed; bottom: 24px; right: 24px; max-width: 360px; background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; box-shadow: 0 4px 12px rgba(0,0,0,0.08); padding: 16px; z-index: 1000; }
.content strong { display: block; margin-bottom: 4px; }
.content p { font-size: 13px; color: #64748b; margin: 0; }
.sound-toggle { display: flex; align-items: center; gap: 8px; margin-top: 10px; font-size: 13px; color: #334155; cursor: pointer; }
.sound-toggle input { margin: 0; }
.actions { display: flex; gap: 8px; margin-top: 12px; justify-content: flex-end; }
.btn-skip, .btn-allow { border: 1px solid #cbd5e1; background: #fff; padding: 6px 14px; border-radius: 6px; cursor: pointer; }
.btn-allow { background: #f97316; border-color: #f97316; color: #fff; }
.btn-allow:disabled { opacity: 0.5; cursor: not-allowed; }
</style>
